import React, { useState, useEffect, useCallback } from 'react';
import { Users, Loader2, Merge, AlertTriangle, Crown, FileText, Scale, Euro, X, Landmark } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { contactosAPI } from '../api';
import { toast } from '../hooks/use-toast';

const groupKey = (g) => Math.min(...g.contactos.map(c => c.id));

const ContactoDuplicadosDialog = ({ open, onOpenChange, onMerged, modo = 'contactos' }) => {
  const esJuzgados = modo === 'juzgados';
  const [loading, setLoading] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [masters, setMasters] = useState({});
  const [mergingIdx, setMergingIdx] = useState(null);
  const [descartandoIdx, setDescartandoIdx] = useState(null);
  const [excluidos, setExcluidos] = useState({});

  const isExcluido = (gkey, id) => (excluidos[gkey] || []).includes(id);

  const toggleExcluir = (grupo, id) => {
    const gkey = groupKey(grupo);
    const cur = excluidos[gkey] || [];
    const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
    setExcluidos(prev => ({ ...prev, [gkey]: next }));
    // Si el maestro queda excluido, moverlo a la primera ficha activa.
    if (next.includes(masters[gkey])) {
      const activo = grupo.contactos.find(c => !next.includes(c.id));
      if (activo) setMasters(prev => ({ ...prev, [gkey]: activo.id }));
    }
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = esJuzgados
        ? await contactosAPI.getDuplicadosJuzgados()
        : await contactosAPI.getDuplicados();
      const gs = res.data?.grupos || [];
      setGrupos(gs);
      const init = {};
      gs.forEach((g) => { init[groupKey(g)] = g.sugerido_master_id; });
      setMasters(init);
      setExcluidos({});
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudieron cargar los duplicados', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [esJuzgados]);

  useEffect(() => {
    if (open) cargar();
  }, [open, cargar]);

  const descartar = async (grupo) => {
    const key = groupKey(grupo);
    const ids = grupo.contactos.map(c => c.id);
    const ok = window.confirm(
      `Marcar este grupo de ${ids.length} fichas como "NO es duplicado".\n\n` +
      `No se modifica ni elimina ninguna ficha; simplemente dejará de aparecer en la detección. ¿Continuar?`
    );
    if (!ok) return;
    setDescartandoIdx(key);
    try {
      await contactosAPI.descartarDuplicado({ ids });
      toast({ title: 'Grupo descartado', description: 'Este grupo ya no volverá a aparecer como duplicado.' });
      setGrupos(prev => prev.filter((g) => groupKey(g) !== key));
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo descartar el grupo';
      toast({ title: 'Error', description: String(msg), variant: 'destructive' });
    } finally {
      setDescartandoIdx(null);
    }
  };

  const fusionar = async (grupo, idx) => {
    const key = groupKey(grupo);
    const masterId = masters[key];
    const excl = excluidos[key] || [];
    const sourceIds = grupo.contactos.map(c => c.id).filter(id => id !== masterId && !excl.includes(id));
    if (!masterId || excl.includes(masterId) || sourceIds.length === 0) return;
    const masterC = grupo.contactos.find(c => c.id === masterId);
    const ok = window.confirm(
      `Se fusionarán ${sourceIds.length} contacto(s) en "${masterC?.nombre_completo}" (${masterC?.id_interno}).\n\n` +
      `Se moverán TODOS los expedientes, facturas y datos relacionados al contacto maestro y los demás se ELIMINARÁN definitivamente.\n\n¿Continuar?`
    );
    if (!ok) return;
    setMergingIdx(key);
    try {
      const res = await contactosAPI.fusionar({ master_id: masterId, source_ids: sourceIds });
      const movidas = res.data?.relaciones_movidas || {};
      const total = Object.values(movidas).reduce((a, b) => a + b, 0);
      toast({
        title: 'Contactos fusionados',
        description: `${sourceIds.length} contacto(s) fusionados en ${masterC?.id_interno}. ${total} relación(es) trasladadas.`
      });
      setGrupos(prev => prev.filter((g) => groupKey(g) !== key));
      onMerged?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo completar la fusión';
      toast({ title: 'Error', description: String(msg), variant: 'destructive' });
    } finally {
      setMergingIdx(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="duplicados-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {esJuzgados ? <Landmark className="w-5 h-5 text-blue-600" /> : <Users className="w-5 h-5 text-blue-600" />}
            {esJuzgados ? 'Duplicados de juzgados' : 'Contactos duplicados'}
          </DialogTitle>
          <DialogDescription>
            {esJuzgados ? (
              <>Fichas del <strong>mismo juzgado</strong> con nombres distintos (agrupadas por población + número de plaza + jurisdicción, según la reforma del Tribunal de Instancia). Elige la ficha con el <strong>nombre correcto actual</strong> como maestro y fusiona; el resto se eliminan tras trasladar sus relaciones.</>
            ) : (
              <>Agrupados por <strong>mismo NIF/CIF</strong> o <strong>mismo nombre y NIF</strong>. Elige el contacto maestro
              (recomendado: el que tiene más expedientes) y fusiona: se trasladan todas las relaciones y se eliminan los duplicados.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-4 min-h-[200px]">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : grupos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500">
              <Users className="w-12 h-12 mb-3 text-gray-300" />
              <p className="font-medium">No se han detectado contactos duplicados</p>
              <p className="text-sm">Todo en orden: no hay coincidencias por NIF/CIF ni por nombre.</p>
            </div>
          ) : (
            grupos.map((grupo, idx) => {
              const gkey = groupKey(grupo);
              const activosCount = grupo.contactos.filter(c => !isExcluido(gkey, c.id)).length;
              return (
              <div key={gkey} className="border rounded-lg overflow-hidden" data-testid={`duplicado-grupo-${idx}`}>
                <div className="flex items-center justify-between px-4 py-2 bg-amber-50 border-b">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">{grupo.motivo}</span>
                    <Badge variant="outline" className="text-xs">{activosCount} de {grupo.contactos.length}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-gray-300 text-gray-700 hover:bg-gray-100"
                      disabled={descartandoIdx === gkey || mergingIdx === gkey}
                      onClick={() => descartar(grupo)}
                      data-testid={`descartar-btn-${idx}`}
                    >
                      {descartandoIdx === gkey ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <X className="w-4 h-4 mr-1" />
                      )}
                      No es duplicado
                    </Button>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                      disabled={mergingIdx === gkey || descartandoIdx === gkey || activosCount < 2}
                      onClick={() => fusionar(grupo, idx)}
                      data-testid={`fusionar-btn-${idx}`}
                    >
                      {mergingIdx === gkey ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Merge className="w-4 h-4 mr-1" />
                      )}
                      Fusionar{activosCount >= 2 ? ` (${activosCount})` : ''}
                    </Button>
                  </div>
                </div>
                <div className="divide-y">
                  {grupo.contactos.map((c) => {
                    const excluido = isExcluido(gkey, c.id);
                    const isMaster = masters[gkey] === c.id && !excluido;
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-3 px-4 py-3 transition-colors ${excluido ? 'bg-gray-50 opacity-60' : (isMaster ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer')}`}
                        data-testid={`duplicado-contacto-${c.id}`}
                      >
                        <input
                          type="radio"
                          name={`master-${gkey}`}
                          checked={isMaster}
                          disabled={excluido}
                          onChange={() => setMasters(prev => ({ ...prev, [gkey]: c.id }))}
                          className="w-4 h-4 text-blue-600"
                          data-testid={`master-radio-${c.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium truncate ${excluido ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{c.nombre_completo}</span>
                            <span className="text-xs text-gray-400">{c.id_interno}</span>
                            {isMaster && (
                              <Badge className="bg-blue-600 text-white text-[10px] gap-1"><Crown className="w-3 h-3" /> Maestro</Badge>
                            )}
                            {excluido && (
                              <Badge variant="outline" className="text-[10px] text-gray-500">Excluido</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                            {c.nif_cif && <span>NIF: {c.nif_cif}</span>}
                            {c.email && <span className="truncate">{c.email}</span>}
                            {c.telefono && <span>{c.telefono}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-600 shrink-0">
                          <span className="flex items-center gap-1" title="Expedientes"><FileText className="w-3.5 h-3.5" />{c.relaciones?.expedientes ?? 0}</span>
                          <span className="flex items-center gap-1" title="Reclamaciones"><Scale className="w-3.5 h-3.5" />{c.relaciones?.reclamaciones ?? 0}</span>
                          <span className="flex items-center gap-1" title="Facturas"><Euro className="w-3.5 h-3.5" />{c.relaciones?.facturas ?? 0}</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className={`h-7 px-2 ${excluido ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-500 hover:bg-gray-100'}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleExcluir(grupo, c.id); }}
                            data-testid={`excluir-btn-${c.id}`}
                          >
                            {excluido ? 'Incluir' : 'Excluir'}
                          </Button>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactoDuplicadosDialog;
